import React, { useState, useEffect } from 'react';
import { Typography, Tag, Space, Button, Tooltip } from 'antd';
import { PushpinFilled, ClockCircleOutlined } from '@ant-design/icons';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useChat } from '../../contexts/ChatContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useUser } from '../../contexts/UserContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { ModelType } from '../../types/models';
import { sampleSidebarService } from '../../services/mock/sampleSidebarService';

const { Text } = Typography;

export const PinnedThreads: React.FC = () => {
  const { dataSource } = useModelSelector();
  const { threads, selectThread: chatSelectThread, unpinThread } = useChat();
  const { selectThread } = useNavigation();
  const { user, isAuthenticated } = useUser();
  const { model: chatUIModel } = useChatUI();
  const [sampleThreads, setSampleThreads] = useState<any[]>([]);

  useEffect(() => {
    if (dataSource === 'sample') {
      void loadSampleThreads();
    }
  }, [dataSource]);

  const loadSampleThreads = async () => {
    try {
      const threads = await sampleSidebarService.getSampleThreads();
      setSampleThreads(threads);
    } catch (error) {
      console.error('Failed to load sample threads:', error);
    }
  };

  const displayThreads = dataSource === 'sample' ? sampleThreads : threads;

  const handleThreadClick = (thread: any) => {
    // Use navigation context to select the thread (it will also handle chat context)
    void selectThread(thread.id);
  };

  const handleUnpin = async (thread: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent thread selection when clicking unpin button
    
    try {
      await unpinThread(thread.id);
    } catch (error) {
      console.error('Failed to unpin thread:', error);
    }
  };

  // Filter threads to show only pinned threads that match ChatUI model context
  const pinnedThreads = displayThreads
    .filter(thread => {
      // First check if pinned
      if (!thread.isPinned) return false;
      
      // Then check if matches ChatUI model context
      if (!chatUIModel || !thread.model) return false;
      
      if (chatUIModel.type === ModelType.PRIME) {
        return thread.model.type === ModelType.PRIME && 
               thread.model.artifactId === chatUIModel.artifactId;
      } else if (chatUIModel.type === ModelType.COMPANY) {
        return thread.model.type === ModelType.COMPANY && 
               thread.model.artifactTreeId === chatUIModel.artifactTreeId;
      }
      return false;
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  if (!isAuthenticated || !user) {
    return (
      <div style={{ textAlign: 'center', padding: 8 }}>
        <Text style={{ color: 'white', fontSize: 11 }}>
          Please select a user
        </Text>
      </div>
    );
  }

  if (pinnedThreads.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 8 }}>
        <Text style={{ color: 'white', fontSize: 11 }}>
          No pinned threads
        </Text>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {pinnedThreads.slice(0, 5).map((thread) => (
        <div
          key={thread.id}
          onClick={() => handleThreadClick(thread)}
          style={{
            padding: '8px 12px',
            marginBottom: '4px',
            backgroundColor: 'rgba(250, 173, 20, 0.2)',
            borderRadius: '6px',
            cursor: 'pointer',
            border: '1px solid rgba(250, 173, 20, 0.3)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(250, 173, 20, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(250, 173, 20, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(250, 173, 20, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(250, 173, 20, 0.3)';
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text 
                style={{ 
                  color: 'white', 
                  fontSize: '12px', 
                  fontWeight: 500,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                title={thread.name}
              >
                {thread.name}
              </Text>
              <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                <Tag color="orange">
                  {thread.messageCount} msgs
                </Tag>
                <Tag color="default">
                  {thread.uiRole}
                </Tag>
              </div>
            </div>
            <Tooltip title="Unpin thread">
              <Button
                type="text"
                size="small"
                icon={<PushpinFilled />}
                onClick={(e) => handleUnpin(thread, e)}
                style={{ 
                  color: '#faad14',
                  padding: '2px 4px',
                  minWidth: 'auto'
                }}
              />
            </Tooltip>
          </div>
        </div>
      ))}
      
      {pinnedThreads.length > 5 && (
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Text style={{ color: 'white', fontSize: 11 }}>
            +{pinnedThreads.length - 5} more pinned
          </Text>
        </div>
      )}
    </div>
  );
};
